import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { serverContext, fetchFromServer } from './App';
import PropTypes from 'prop-types';


export const useServerGet = (url, server, updated) => {
  const [data, setData] = useState(null);
  const [errorRet, setErrorRet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await axios(
          `${server.serverHost}/api/${url}`,
          {
            headers: { Authorization: `Basic ${server.token}` },
            cancelToken: source.token,
          }
        );
        if (isMounted && res?.data) {
          setData(res.data);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          // request was cancelled—no need to do anything
        } else if (isMounted) {
          if (error.response) {
            setErrorRet(`Server Error: ${error.response.status} - ${error.response.statusText}`);
          } else if (error.request) {
            setErrorRet('No response received from the server.');
          } else {
            setErrorRet('An error occurred while making the request.');
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (server.serverHost) {
      fetchData();
    }

    return () => {
      // cleanup: prevent state updates and cancel the request
      isMounted = false;
      source.cancel('Component unmounted');
    };
  }, [server, server.serverHost, url, updated]);

  return { data, errorRet, isLoading };
};

/**
 * GetPlatforms
 */
// export const useGetPlatforms = () => {
//     const { server } = useContext(serverContext);
//     const { dispatchPlatforms } = useContext(platformsContext);

//     useEffect(() => {
//         const fetch = async () => {

//             if (server.serverHost) {
//                 try {
//                     const servePlatforms = await fetchFromServer('platforms', server);
//                     dispatchPlatforms({ type: 'SET_PLATFORMS', payload: servePlatforms.data });
//                 } catch (error) {
//                     dispatchPlatforms({ type: 'SET_PLATFORMS', payload: null });
//                 }
//             }
//         }

//         fetch();
//     }, [server.serverHost, server, dispatchPlatforms]);
// };



export const useGetUsergroups = async () => {

    const { server, dispatchServer } = useContext(serverContext);

    useEffect(() => {
        const fetch = async () => {
            if (server.serverHost && server.usergroups == null) {
                try {
                    const res = await fetchFromServer('usergroups', server);
                    dispatchServer({ type: 'SET_USERGROUPS', payload: res.data });

                } catch (error) {
                    dispatchServer({ type: 'SET_USERGROUPS', payload: null });
                }
            }
        };
        fetch();
    }, [server.serverHost, server.usergroups, dispatchServer, server]);
};


export const useGetUsers = async () => {

    const { server, dispatchServer } = useContext(serverContext);

    useEffect(() => {
        const fetch = async () => {
            if (server.serverHost && server.users == null) {
                try {
                    const res = await fetchFromServer('users', server);
                    dispatchServer({ type: 'SET_USERS', payload: res.data });

                } catch (error) {
                    dispatchServer({ type: 'SET_USERS', payload: null });
                }
            }
        };
        fetch();
    }, [server.serverHost, server.users, dispatchServer, server]);
};



export const useFetchImageFromVideoServer = (imageUrl, accessToken, update) => {
    const [image, setImage] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        async function fetchImage(accessToken) {

            const headers = accessToken && {
                headers: {
                    'authorization': `Bearer ${accessToken}`,
                }
            }

            const response = await fetch(imageUrl, headers);
            const blob = await response.blob();
            const dataUrl = await new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(blob);
            });
            return dataUrl;
        }

        async function fetchData() {

            try {
                const dataUrl = await fetchImage(accessToken);
                setImage(dataUrl);
            }
            catch (error) {
                console.error('Error fetching image:', error);
                setHasError(true);
            }
        }
        fetchData();

        setIsLoading(false);

    }, [imageUrl, accessToken, update]);


    return { isLoading, hasError, image };
}



export const useDocuments = (serverUrl, serverToken, filter, docsUpdateTime) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState({ error: false, message: undefined });

    useEffect(() => {
        const getData = async () => {
            if (!serverUrl || !serverToken) return;

            setIsLoading(true);
            try {
                const response = await axios.get(`${serverUrl}/api/documents`, {
                    params: filter,
                    headers: { 'Authorization': `Basic ${serverToken}` }
                });

                setDocuments(response.data);
                setHasError({ error: false, message: undefined });
            } catch (error) {
                console.error(`useDocuments error: ${error.message}`);
                setDocuments([]);
                setHasError({
                    error: true,
                    message: error.response?.data?.message || error.message || 'An error occurred'
                });
            } finally {
                setIsLoading(false);
            }
        };

        getData();
    }, [serverUrl, serverToken, filter, docsUpdateTime]);

    return { isLoading, hasError, documents };
};


export const useTags = (serverUrl, serverToken, category, tagsUpdateTime) => {
    const [tags, setTags] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState({ error: false, message: undefined });

    useEffect(() => {
        const getData = async () => {
            if (!serverUrl || !serverToken) return;

            setIsLoading(true);
            try {
                const response = await axios.get(`${serverUrl}/api/tags`, {
                    params: category ? { category } : {},
                    headers: { 'Authorization': `Basic ${serverToken}` }
                });

                setTags(response.data);
                setHasError({ error: false, message: undefined });
            } catch (error) {
                console.error(`useTags error: ${error.message}`);
                setTags([]);
                setHasError({
                    error: true,
                    message: error.response?.data?.message || error.message || 'An error occurred'
                });
            } finally {
                setIsLoading(false);
            }
        };

        getData();
    }, [serverUrl, serverToken, category, tagsUpdateTime]);

    return { isLoading, hasError, tags };
};


export const useAisData = (serverUrl, serverToken, filter, updateTime) => {
    const [aisData, setAisData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState({ error: false, message: undefined });

    useEffect(() => {
        let isMounted = true;

        const getData = async () => {
            if (!serverUrl || !serverToken) return;

            setIsLoading(true);
            try {
                const endpoint = filter.aisType === 'vessels' ? 'aistargets' : 'aisbasestations';
                const response = await axios.get(`${serverUrl}/api/${endpoint}`, {
                    params: filter,
                    headers: { 'Authorization': `Basic ${serverToken}` }
                });

                if (isMounted) {
                    setAisData(response.data);
                    setHasError({ error: false, message: undefined });
                }
            } catch (error) {
                if (isMounted) {
                    console.error(`useAisData error: ${error.message}`);
                    setAisData([]);
                    setHasError({
                        error: true,
                        message: error.response?.data?.message || error.message || 'An error occurred'
                    });
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        getData();

        return () => {
            isMounted = false;
        };
    }, [serverUrl, serverToken, JSON.stringify(filter), updateTime]);

    return { isLoading, hasError, aisData };
};

useAisData.propTypes = {
    serverUrl: PropTypes.string.isRequired,
    serverToken: PropTypes.string.isRequired,
    filter: PropTypes.shape({
        aisType: PropTypes.string
    }),
    updateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const flattenBbox = (bbox) => {
    const result = {};
    for (const [key, value] of Object.entries(bbox)) {
        result[`bbox[${key}]`] = value;
    }
    return result;
};


export const useAnnotationData = (serverUrl, serverToken, filter, updateTime) => {
    const [annotationData, setAnnotationData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState({ error: false, message: undefined });

    useEffect(() => {
        let isMounted = true;

        const getData = async () => {
            if (!serverUrl || !serverToken) return;

            setIsLoading(true);
            try {

                const flattenedBoundingBox = filter?.bbox ? flattenBbox(filter.bbox) : undefined;
                const baseFilter = filter && typeof filter === 'object' ? { ...filter } : {};
                if (baseFilter.bbox) {
                    delete baseFilter.bbox;
                }
                const flattenedFilter = {
                    ...baseFilter,
                    ...(flattenedBoundingBox || {})
                };
    
                const response = await axios.get(`${serverUrl}/api/annotations`, {
                    params: flattenedFilter,
                    headers: { 'Authorization': `Basic ${serverToken}` }
                });

                if (isMounted) {
                    setAnnotationData(response.data);
                    setHasError({ error: false, message: undefined });
                }
            } catch (error) {
                if (isMounted) {
                    console.error(`useAnnotationData error: ${error.message}`);
                    setAnnotationData([]);
                    setHasError({
                        error: true,
                        message: error.response?.data?.message || error.message || 'An error occurred'
                    });
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        getData();

        return () => {
            isMounted = false;
        };
    }, [serverUrl, serverToken, filter, updateTime]);

    return { isLoading, hasError, annotationData };
};

useAnnotationData.propTypes = {
    serverUrl: PropTypes.string.isRequired,
    serverToken: PropTypes.string.isRequired,
    filter: PropTypes.shape({
        aisType: PropTypes.string
    }),
    updateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};



export const useAisTrail = (serverUrl, serverToken, mmsi, filter, updateTime) => {
    const [selectedAisTrail, setSelectedAisTrail] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState({ error: false, message: undefined });

    useEffect(() => {
        let isMounted = true;

        const getData = async () => {
            if (!serverUrl || !serverToken) return;

            setIsLoading(true);
            try {

                const response = await axios.get(`${serverUrl}/api/aistargets/trail/${mmsi}`, {
                    params: filter,
                    headers: { 'Authorization': `Basic ${serverToken}` }
                });

                if (isMounted) {
                    setSelectedAisTrail(response.data);
                    setHasError({ error: false, message: undefined });
                }
            } catch (error) {
                if (isMounted) {
                    console.error(`useAisTrail error: ${error.message}`);
                    setSelectedAisTrail([]);
                    setHasError({
                        error: true,
                        message: error.response?.data?.message || error.message || 'An error occurred'
                    });
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (mmsi != null) {
            getData();
        }

        return () => {
            isMounted = false;
        };
    }, [serverUrl, serverToken, mmsi, JSON.stringify(filter), updateTime]);

    return { isLoading, hasError, selectedAisTrail };
};

useAisTrail.propTypes = {
    serverUrl: PropTypes.string.isRequired,
    serverToken: PropTypes.string.isRequired,
    mmsi: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]).isRequired,
    filter: PropTypes.object,
    updateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])])
};
